import { memo, useEffect } from 'react'
import styled from 'styled-components'

/**
 * The board's own modal.
 *
 * The app has modals of its own and they work, but they are drawn in the
 * terminal's language — rounded cards and roomy rows — which reads as a
 * different product once it opens over this board. This is the same frame the
 * board uses elsewhere: panel over line over chip, Geist, tight rows.
 *
 * On a phone it rises from the bottom edge rather than sitting in the middle,
 * for the same reason the account menu does.
 */
export const PulseModal: React.FC<{
  title: string
  onClose: () => void
  children: React.ReactNode
  /** A word under the title, when the modal needs one. */
  note?: string
  /** Shown as an arrow beside the title, for a step inside a window. */
  onBack?: () => void
  width?: number
}> = memo(({ title, note, onClose, onBack, children, width = 460 }) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <Root>
      <span className="veil" onClick={onClose} />
      <div
        className="panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ width: `${width}px` }}
      >
        <div className="head">
          {onBack ? (
            <button
              className="x back"
              type="button"
              aria-label="Back"
              onClick={onBack}
            >
              <svg viewBox="0 0 24 24">
                <path d="M15 6l-6 6 6 6" />
              </svg>
            </button>
          ) : undefined}
          <span className="ttl">{title}</span>
          <button
            className="x"
            type="button"
            aria-label="Close"
            onClick={onClose}
          >
            <svg viewBox="0 0 24 24">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        {note ? <p className="note">{note}</p> : undefined}
        <div className="body">{children}</div>
      </div>
    </Root>
  )
})

/** One line of a modal: a name, what it is, and the thing you press. */
export const PulseRow: React.FC<{
  title: string
  note?: string
  children?: React.ReactNode
  tone?: 'plain' | 'warn'
}> = memo(({ title, note, children, tone = 'plain' }) => (
  <div className="row" data-tone={tone}>
    <span className="rowtext">
      <b>{title}</b>
      {note ? <i>{note}</i> : undefined}
    </span>
    {children ? <span className="rowact">{children}</span> : undefined}
  </div>
))

const Root = styled.div`
  .veil {
    position: fixed;
    inset: 0;
    z-index: 40;
    background: rgba(6, 7, 11, 0.72);
  }

  .panel {
    position: fixed;
    z-index: 41;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: calc(100vw - 32px);
    max-height: calc(100vh - 80px);
    background: var(--panel);
    border: 1px solid var(--line);
    border-radius: 10px;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.66);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .head {
    flex-shrink: 0;
    height: 46px;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 0 10px 0 16px;
    border-bottom: 1px solid var(--line);
  }

  .back {
    margin-left: -8px;
  }

  .ttl {
    flex-grow: 1;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.2px;
    color: var(--t1);
  }

  .x {
    width: 26px;
    height: 26px;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: 5px;
    background: none;
    color: var(--t4);
    cursor: pointer;
  }

  .x:hover {
    background: var(--chip);
    color: var(--t1);
  }

  .x svg {
    width: 13px;
    height: 13px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
  }

  .note {
    margin: 0;
    padding: 12px 16px 0;
    font-size: 12.5px;
    line-height: 19px;
    color: var(--t4);
  }

  .body {
    flex-grow: 1;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: none;
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .body::-webkit-scrollbar {
    display: none;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 48px;
    padding: 10px 12px;
    border: 1px solid var(--line);
    border-radius: 7px;
    background: var(--head);
  }

  .rowtext {
    flex-grow: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .rowtext b {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: -0.1px;
    color: var(--t1);
  }

  .rowtext i {
    font-style: normal;
    font-size: 12px;
    line-height: 17px;
    color: var(--t4);
  }

  .row[data-tone='warn'] .rowtext b {
    color: var(--down);
  }

  .rowact {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .act {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 30px;
    padding: 0 11px;
    border: 1px solid var(--chip-hi);
    border-radius: 5px;
    background: var(--chip);
    color: var(--t2);
    font-family: inherit;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
  }

  .act:hover {
    background: var(--chip-hi);
    color: var(--t1);
  }

  .act svg {
    width: 13px;
    height: 13px;
    flex-shrink: 0;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .act[data-tone='warn'] {
    border-color: rgba(225, 92, 92, 0.3);
    background: rgba(225, 92, 92, 0.1);
    color: var(--down);
  }

  .act[data-tone='warn']:hover {
    background: rgba(225, 92, 92, 0.16);
    color: var(--down);
  }

  .dot {
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    background: var(--accent);
    color: var(--t1);
    font-family: 'Geist Mono', ui-monospace, monospace;
    font-size: 11px;
    font-weight: 600;
  }

  .pick {
    height: 30px;
    padding: 0 8px;
    border: 1px solid var(--chip-hi);
    border-radius: 5px;
    background: var(--chip);
    color: var(--t2);
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
  }

  @container pulse (max-width: 759px) {
    .panel {
      top: auto;
      bottom: 0;
      left: 0;
      transform: none;
      width: auto !important;
      max-width: none;
      max-height: 86vh;
      border-radius: 14px 14px 0 0;
      border-bottom: 0;
      padding-bottom: env(safe-area-inset-bottom, 0px);
    }

    .head {
      height: 52px;
    }

    .row {
      min-height: 56px;
    }
  }
`
